import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import validator from "validator";
import { forgotPasswordUser } from "../../services/firebase";
import { withStyles } from "@material-ui/core/styles";
import AuthContext from "../../hooks/authContext";
import loginBg from "../../assets/images/pc_loginBg.jpg";
import { Mail, ArrowLeft } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import AuthLayout from "../../components/auth/AuthLayout";
import AuthSideContent from "../../components/auth/AuthSideContent";

const styles = (theme) => ({
  pageroot: {
    background: "url(" + loginBg + ") top center no-repeat",
    backgroundSize: "cover",
    backgroundAttachment: "fixed",
    height: "100vh",
    paddingTop: theme.spacing(2),
  },
  paper: {},
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(4),
  },
  submit: {
    margin: theme.spacing(4, 0, 2),
    background: "#078037",
    color: "#fff",
    "&:hover": {
      background: "#078037",
    },
  },
});
const ForgotPassword = (props) => {
  let navigate = useNavigate();
  const auth = useContext(AuthContext);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, showInfoMessage] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loader, setLoader] = useState(false);
  const [state, setState] = useState({
    formState: { email: "", password: "" },
    loading: false,
    sRedirect: "",
    terms: "",
  });

  const handleInputChange = (e) => {
    setErrorMessage("");
    showInfoMessage("");
    const { name, value } = e.target;
    if (name !== "") {
      setState({ ...state, formState: { ...state.formState, [name]: value } });
    }
  };
  const redirecifloggedin = () => {
    navigate("/dashboard");
  };

  useEffect(() => {
    if (auth.status === true) {
      redirecifloggedin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.status]);

  const showErrorMessage = (message) => {
    setErrorMessage(message);
    setState({ ...state, sRedirect: "", loading: false });
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const SendRecoveryLink = async (e) => {
    setLoader(true);
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setState({ ...state, loading: true });

    let { email } = state.formState;
    email = email?.trim();

    if (email !== "") {
      const isValidEmail =   /^[A-Za-z0-9._%+-]+@(?:(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)\.)+[A-Za-z]{2,6}$/i.test(email);
      if (!isValidEmail) {
        setLoader(false);
        showErrorMessage("Email is not Valid");
      } else {
        await forgotPasswordUser(
          email,
          (resSuccess) => {
            setState({ ...state, loading: false });
            if (resSuccess?.message === "Account found") {
              showInfoMessage(`We have received your request to reset the password for ${email}. If this email address is in our system you will receive an email confirmation with next step. Thank you for your request!`)
            } else if (resSuccess?.message === "Password reset email sent!") {
              showInfoMessage("");
              setSuccessMessage("Reset Password Link sent to your Email, Redirecting to Login...");
              setIsSubmitted(true);
            }
          },
          (resError) => {
            setState({ ...state, loading: false });
            setLoader(false);
            showErrorMessage(resError?.message || "Something Went Wrong!");
          }
        );
      }
    } else {
      setLoader(false);
      showErrorMessage("Email Fields is required!");
    }
    setLoader(false);
    setState({ ...state, loading: false });
  };

  const { formState, loading, sRedirect } = state;
  const { classes } = props;

  const features = [
    "Streamlined team management",
    "Improved client communication",
    "Efficient appointment scheduling",
    "Seamless invoice management"
  ];

  const testimonial = {
    quote: "ProTeams has transformed how we manage our service teams. Our scheduling is more efficient and our clients are happier with the improved communication.",
    name: "Sarah Johnson",
    role: "CEO, CleanPro Services",
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter your email to receive password reset instructions"
      sideContent={
        <AuthSideContent
          title="Modernize your service business"
          features={features}
          testimonial={testimonial}
        />
      }
    >
      <div className="w-full px-4 sm:px-0">
      {isSubmitted === true ? (
        <div className="space-y-5 sm:space-y-6">
          <div className="bg-green-50 border border-green-200 p-8 rounded-md">
            <h3 className="font-medium text-green-800 mb-4">
              Check your email
            </h3>
            <p className="text-green-700 text-sm">
              We've sent password reset instructions to the email address
              provided.
            </p>
          </div>

            <Button
              type="button"
              className="w-full"
              onClick={handleBackToLogin}
              size="lg"
            >
              Back to login
            </Button>
          </div>
        ) : (
          <div className="space-y-5 sm:space-y-6">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium">
                Email Address
              </label>
              <div className="relative input">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  fullWidth={true}
                  disableUnderline={true}
                  onChange={handleInputChange}
                  value={formState?.email}
                  autoComplete="email"
                  className={`pl-35 rounded-md ${
                    errorMessage !== ""
                      ? "ring-1 ring-[#dc2626] focus:ring-2 focus:ring-[#dc2626]"
                      : "ring-1 ring-gray-300 focus:ring-2 focus:ring-[#2980ba]"
                  }`}
                  name="email"
                  id="email"
                  placeholder="Email"
                />
              </div>
              <p className="text-sm text-gray-500 mt-8">
                Enter the email address associated with your account, and we'll
                send you instructions to reset your password.
              </p>
            </div>

            <div className="flex justify-center items-center mt-20 mb-0">
              {errorMessage !== "" ? (
                <span className="text-[14px] font-medium text-[#dc2626]">
                  {errorMessage === "No User found with this email"
                    ? "No active account found for this email address."
                    : errorMessage}
                </span>
              ) : errorMessage === "" && infoMessage !== "" ? (
                <span className="text-[14px] font-medium text-[#078037]">
                  {infoMessage}
                </span>
              ) : null}
            </div>

            <Button
              type="button"
              className="w-full"
              onClick={SendRecoveryLink}
              size="lg"
            >
              {loader ? (
                <CircularProgress size={22} style={{ color: "white" }} />
              ) : (
                "Send reset instructions"
              )}
            </Button>

            <div className="text-center">
              <Button
                type="button"
                variant="ghost"
                className="text-sm flex items-center mx-auto"
                onClick={handleBackToLogin}
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to login
              </Button>
            </div>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

// return (
//   <div className={classes.pageroot}>
//     {sRedirect !== "" && <Navigate to={sRedirect} />}
//     <Container component="main" maxWidth="xs">
//       <CssBaseline />
//       <div className={`${classes.paper} loginwrap sectionBox`}>
//         <img className="cmslogo" src={cmsAdminLogo} alt="PROTeam Login" />
//         <form className={classes.form} noValidate>
//           <div className="formFieldGroup">
//             <Input
//               fullWidth={true}
//               disableUnderline={true}
//               onChange={handleInputChange}
//               value={formState.email}
//               autoComplete="email"
//               className="input"
//               name="email"
//               id="email"
//               placeholder="Email"
//               startAdornment={<span className="input-adornment"><FontAwesomeIcon icon={faUser} /></span>}
//             />
//           </div>
//           <Button
//             type="submit"
//             fullWidth={true}
//             className={`${classes.submit}`}
//             color={"primary"}
//             variant={"contained"}
//             onClick={SendRecoveryLink}
//             disabled={loading ? true : false}
//           >
//             {loading ? "Loading..." : "RESET PASSWORD"}
//           </Button>
//           <Typography className="text-center mt-25" variant="body2">
//             Already Registred ? <Link className="color-blue" to="/login"> Click here</Link>
//           </Typography>
//           {errorMessage !== "" && (
//             <Typography className="text-center" color="error" variant="body2">
//               {errorMessage}
//             </Typography>
//           )}
//           {successMessage !== "" && (
//             <Typography className="text-center" color="primary" variant="body2">
//               {successMessage}
//             </Typography>
//           )}
//         </form>
//       </div>
//     </Container>
//   </div>
// );

export default withStyles(styles, { withTheme: true })(ForgotPassword);
